import React, { useEffect, useState } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { ChartBarIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useCompetencyEvidence } from '../../hooks/useCompetencyEvidence';
import { motion, AnimatePresence } from 'framer-motion';

interface Competency {
  id: string;
  name: string;
  description: string;
  weight: number;
  focus_id: string;
  order_index: number;
  is_custom: boolean;
  template_id?: string;
  created_at: string;
}

interface CompetencyCoverageProps {
  competencies: Competency[];
  cards: any[];
  onAddEvidence: () => void;
  focusId?: string;
}

const CompetencyCoverage: React.FC<CompetencyCoverageProps> = ({
  competencies,
  cards,
  onAddEvidence,
  focusId
}) => {
  const { darkMode } = useDarkMode();
  const { evidence, getCompetencyCoverage } = useCompetencyEvidence({ focusId });
  const [coverageData, setCoverageData] = useState<Record<string, number>>({});
  const [isExpanded, setIsExpanded] = useState(false);
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  
  // Calculate coverage for each competency
  useEffect(() => {
    if (competencies && evidence) {
      const coverage: Record<string, number> = {};
      competencies.forEach(comp => {
        const count = getCompetencyCoverage(comp.id);
        // Scale coverage to a percentage (assuming 5 pieces of evidence = 100%)
        coverage[comp.id] = Math.min((count / 5) * 100, 100);
      });
      setCoverageData(coverage);
    }
  }, [competencies, evidence]);
  
  // Check if we have competencies
  if (!competencies || competencies.length === 0) {
    return (
      <div className={`${bgColor} ${borderColor} border rounded-lg shadow-sm p-6`}>
        <h3 className={`text-lg font-semibold ${textColor} mb-4`}>Competency Coverage</h3>
        <div className="text-center py-8">
          <ChartBarIcon className={`w-12 h-12 mx-auto ${mutedTextColor} mb-3`} />
          <p className={`${mutedTextColor} text-sm`}>
            Coming Soon
          </p>
          <p className={`${mutedTextColor} text-xs mt-2`}>
            Complete your setup to see competency coverage
          </p>
        </div>
      </div>
    );
  }
  
  // Determine which competencies to show
  const visibleCompetencies = isExpanded ? competencies : competencies.slice(0, 4);
  const hasMoreCompetencies = competencies.length > 4;
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg shadow-sm p-6`}>
      <h3 className={`text-lg font-semibold ${textColor} mb-4`}>Competency Coverage</h3>
      
      <div className="space-y-3">
        <AnimatePresence initial={false}>
          {visibleCompetencies.map((competency, index) => {
          const coverage = coverageData[competency.id] || 0;
          const evidenceCount = Math.floor(coverage / 20); // Convert back to count
          
          return (
            <motion.div
              key={competency.id}
              initial={index >= 4 ? { opacity: 0, height: 0 } : false}
              animate={{ opacity: 1, height: 'auto' }}
              exit={index >= 4 ? { opacity: 0, height: 0 } : {}}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-between items-center mb-1">
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {competency.name}
                </span>
                <span className={`text-xs ${mutedTextColor}`}>
                  {evidenceCount === 0 ? 'No evidence yet' : `${evidenceCount} evidence`}
                </span>
              </div>
              <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2 mb-3`}>
                <div 
                  className={`${
                    coverage > 0 
                      ? coverage >= 80 ? 'bg-green-500' : coverage >= 40 ? 'bg-yellow-500' : 'bg-orange-500'
                      : 'bg-gray-400'
                  } h-2 rounded-full transition-all duration-300`}
                  style={{ width: `${coverage}%` }}
                />
              </div>
            </motion.div>
          );
          })}
        </AnimatePresence>
      </div>
      
      <div className="mt-4 flex justify-between items-center">
        <button
          onClick={onAddEvidence}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          Add evidence →
        </button>
        
        {hasMoreCompetencies && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`text-xs ${mutedTextColor} hover:${textColor} transition-colors`}
          >
            {isExpanded ? 'Show less' : `+${competencies.length - 4} more competencies`}
          </button>
        )}
      </div>
    </div>
  );
};

export default CompetencyCoverage;